import { useQuery } from '@tanstack/react-query'
import { sdApi } from '@/lib/api'
import { getTenant } from './tenants'
import type { StateTenant } from './types'

/**
 * Resolve a tenant theme that is NOT one of the hardcoded fast-path tenants in
 * `tenants.ts`. Partners provisioned through the partner portal live in the
 * scamdefense-api database and are fetched on demand from
 * `GET /public/tenant/:slug`. Hardcoded tenants are handled synchronously by
 * `getTenant()` and skip this fetch entirely.
 *
 * Returns null while loading, on error, or for unknown/unpublished slugs, so
 * the site renders its base (unbranded) pack and never blocks on the network.
 * The branded theme pops in a beat later, which is the correct progressive
 * behaviour for a provisioned subdomain.
 *
 * Spec: docs/specs/partner-portal.md §6.
 */
export function useRemoteTenant(tenantId: string | null): StateTenant | null {
  const isHardcoded = tenantId != null && getTenant(tenantId) != null
  const enabled = tenantId != null && !isHardcoded

  const { data } = useQuery({
    queryKey: ['remote-tenant', tenantId],
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async (): Promise<StateTenant | null> => {
      try {
        const { data } = await sdApi.get(`/public/tenant/${tenantId}`)
        const t = data?.tenant
        if (!t || typeof t.brandName !== 'string') return null
        return {
          id: t.id,
          brandName: t.brandName,
          tagline: t.tagline ?? undefined,
          primaryColor: t.primaryColor ?? undefined,
          logoUrl: t.logoUrl ?? undefined,
          attributionMode: t.attributionMode ?? 'powered-by',
          hotline: t.hotline ?? undefined,
          sponsorStrip: t.sponsorStrip ?? undefined,
        }
      } catch {
        return null
      }
    },
  })

  return data ?? null
}
